import { Material } from 'three';
import hash from 'object-hash';

var keywords = {
  position: 'csm_Position',
  positionRaw: 'csm_PositionRaw',
  emissive: 'csm_Emissive',
  normal: 'csm_Normal',
  pointSize: 'csm_PointSize',
  diffuseColor: 'csm_DiffuseColor',
  fragColor: 'csm_FragColor'
};

const VERT = {
  [`${keywords.normal}`]: {
    '#include <beginnormal_vertex>': `
    vec3 objectNormal = ${keywords.normal};
    #ifdef USE_TANGENT
	    vec3 objectTangent = vec3( tangent.xyz );
    #endif
    `
  },
  [`${keywords.position}`]: {
    '#include <begin_vertex>': `
    vec3 transformed = ${keywords.position};
  `
  },
  [`${keywords.positionRaw}`]: {
    '#include <begin_vertex>': `
    vec4 csm_positionUnprojected = ${keywords.positionRaw};
    mat4x4 csm_unprojectMatrix = projectionMatrix * modelViewMatrix;
    #ifdef USE_INSTANCING
      csm_unprojectMatrix = csm_unprojectMatrix * instanceMatrix;
    #endif
    csm_positionUnprojected = inverse(csm_unprojectMatrix) * csm_positionUnprojected;
    vec3 transformed = csm_positionUnprojected.xyz;
  `
  },
  [`${keywords.pointSize}`]: {
    'gl_PointSize = size;': `
    gl_PointSize = ${keywords.pointSize};
    `
  }
};
const FRAG = {
  [`${keywords.diffuseColor}`]: {
    '#include <color_fragment>': `
    #include <color_fragment>
    diffuseColor = ${keywords.diffuseColor};
  `
  },
  [`${keywords.fragColor}`]: {
    '#include <output_fragment>': `
    #include <output_fragment>
    gl_FragColor  = ${keywords.fragColor};
  `
  },
  [`${keywords.emissive}`]: {
    'vec3 totalEmissiveRadiance = emissive;': `
    vec3 totalEmissiveRadiance = ${keywords.emissive};
    `
  }
};

const replaceAll = (str, find, rep) => str.split(find).join(rep);

const escapeRegExpMatch = function (s) {
  return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
};

const isExactMatch = (str, match) => {
  return new RegExp(`\\b${escapeRegExpMatch(match)}\\b`).test(str);
};

class CustomShaderMaterial extends Material {
  constructor({
    baseMaterial,
    fragmentShader,
    vertexShader,
    uniforms,
    cacheKey,
    ...opts
  }) {
    const base = new baseMaterial(opts);
    super();
    this.uniforms = uniforms || {};

    for (const key in base) {
      let k = key;

      if (key.startsWith('_')) {
        k = key.split('_')[1];
      } // @ts-ignore


      if (this[k] === undefined) this[k] = 0; // @ts-ignore

      this[k] = base[k];
    }

    this.update({
      fragmentShader,
      vertexShader,
      uniforms,
      cacheKey
    });
  }

  update({
    fragmentShader,
    vertexShader,
    uniforms,
    cacheKey
  }) {
    const serializedUniforms = Object.values(uniforms || {}).forEach(({
      value
    }) => {
      return JSON.stringify(value);
    });
    this.uuid = (cacheKey == null ? void 0 : cacheKey()) || hash([fragmentShader, vertexShader, serializedUniforms]);
    this.generateMaterial({
      fragmentShader,
      vertexShader,
      uniforms
    });
  }

  generateMaterial({
    fragmentShader,
    vertexShader,
    uniforms
  }) {
    const parsedFragmentShdaer = this.parseShader(fragmentShader);
    const parsedVertexShdaer = this.parseShader(vertexShader);
    this.uniforms = uniforms || {};

    this.customProgramCacheKey = () => {
      return this.uuid;
    };

    this.onBeforeCompile = shader => {
      if (parsedFragmentShdaer) {
        const patchedFragmentShdaer = this.patchShader(parsedFragmentShdaer, shader.fragmentShader, FRAG);
        shader.fragmentShader = patchedFragmentShdaer;
      }

      if (parsedVertexShdaer) {
        const patchedVertexShdaer = this.patchShader(parsedVertexShdaer, shader.vertexShader, VERT);
        shader.vertexShader = '#define IS_VERTEX;\n' + patchedVertexShdaer;
      }

      shader.uniforms = { ...shader.uniforms,
        ...this.uniforms
      };
      this.uniforms = shader.uniforms;
    };

    this.needsUpdate = true;
  }

  patchShader(customShader, shader, patchMap) {
    let patchedShader = shader;
    Object.keys(patchMap).forEach(name => {
      Object.keys(patchMap[name]).forEach(key => {
        if (isExactMatch(customShader.main, name)) {
          patchedShader = replaceAll(patchedShader, key, patchMap[name][key]);
        }
      });
    });
    patchedShader = patchedShader.replace('void main() {', `
          ${customShader.header}
          void main() {
            vec3 csm_Position;
            vec4 csm_PositionRaw;
            vec3 csm_Normal;
            vec3 csm_Emissive;

            #ifdef IS_VERTEX
              csm_Position = position;
              csm_PositionRaw = projectionMatrix * modelViewMatrix * vec4(position, 1.);
            #endif

            #ifdef IS_VERTEX
              csm_Normal = normal;
            #endif
            
            #ifndef IS_VERTEX
              #ifdef STANDARD
                csm_Emissive = emissive;
              #endif
            #endif

            vec4 csm_DiffuseColor = vec4(1., 0., 0., 1.);
            vec4 csm_FragColor = vec4(1., 0., 0., 1.);
            float csm_PointSize = 1.;

            ${customShader.main}
          `);
    patchedShader = customShader.defines + patchedShader;
    return patchedShader;
  }

  parseShader(shader) {
    if (!shader) return;
    const parsedShader = {
      defines: '',
      header: '',
      main: ''
    };
    const main = shader.match(/^(\s*)(void\s*main\s*\(.*\)\s*).*?{[\s\S]*?^\1}\s*$/gm);

    if (main != null && main.length) {
      const mainBody = main[0].match(/{[\w\W\s\S]*}/gm);

      if (mainBody != null && mainBody.length) {
        parsedShader.main = mainBody[0];
      }

      const rest = shader.replace(main[0], '');
      const defines = rest.match(/#(.*?;)/g) || [];
      const header = defines.reduce((prev, curr) => prev.replace(curr, ''), rest);
      parsedShader.header = header;
      parsedShader.defines = defines.join('\n');
    }

    return parsedShader;
  }

}

export { CustomShaderMaterial as default };
